import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useRef, useState } from "react";

import { createChat } from "../../../actions/chatActions";

// DetailOwnerCard — section 9 left. Canonical detail.html lines 631–652.
//
// Owner href: same convention as PropertyCard.ownerHref —
// `/user/{handle}` when handle present. Locally derived (no shared
// helper) per CLAUDE.md SR-6.
//
// REALTY-PRESENTATION-CONTACTS-A1:
//   * Phone / email — permission-aware from Property detail owner payload.
//     Omit when absent/denied; never render disabled em-dash placeholders.
//   * Chat — authenticated viewers use existing createChat([ownerId]) and
//     navigate to `/chats/{actualChatId}` only. Never invent `/chats/{userId}`.
//   * Anonymous — login CTA with validated `/realty/:id` return target.

const FALLBACK_OWNER_AVATAR = "/images/no-profile-picture.png";
const CHAT_ERROR_UA =
  "Не вдалося створити чат. Можливо, користувач обмежив спілкування.";

function DetailOwnerCard({ property, canContact, isAuthenticated = false }) {
  const owner = property.created_by || null;
  const ownerName =
    (owner && String(owner.fullname || "").trim()) || "Користувач";
  const ownerAvatar = (owner && owner.user_image) || FALLBACK_OWNER_AVATAR;
  const ownerHandle = owner && (owner.handle || owner.public_id);
  const ownerHref = ownerHandle ? `/user/${ownerHandle}` : "#";
  const ownerUserId = owner && owner.id != null ? owner.id : null;
  const phone =
    owner && typeof owner.phone_number === "string"
      ? owner.phone_number.trim()
      : owner && owner.phone_number
        ? String(owner.phone_number).trim()
        : "";
  const email =
    owner && typeof owner.email === "string"
      ? owner.email.trim()
      : owner && owner.email
        ? String(owner.email).trim()
        : "";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const chatInFlightRef = useRef(false);
  const [chatPending, setChatPending] = useState(false);
  const [chatError, setChatError] = useState(null);

  const loginHref =
    property && property.id != null
      ? `/login?next=${encodeURIComponent(`/realty/${property.id}`)}`
      : "/login";

  const handleChatClick = async () => {
    if (!isAuthenticated || ownerUserId == null) return;
    if (chatInFlightRef.current) return;

    chatInFlightRef.current = true;
    setChatPending(true);
    setChatError(null);

    try {
      const createdChat = await dispatch(
        createChat([ownerUserId], undefined, undefined, {
          publishTransientState: false,
        }),
      );

      if (createdChat && createdChat.id != null && createdChat.id !== "") {
        navigate(`/chats/${createdChat.id}`);
        return;
      }

      setChatError(CHAT_ERROR_UA);
    } finally {
      chatInFlightRef.current = false;
      setChatPending(false);
    }
  };

  return (
    <article className="owner-card">
      <Link
        to={ownerHref}
        className="owner-card__photo"
        aria-label="Профіль рієлтора"
      >
        <img src={ownerAvatar} alt="" />
      </Link>
      <div className="owner-card__body">
        <h3 className="owner-card__name">
          <Link to={ownerHref} className="text-decoration-none">
            {ownerName}
          </Link>
        </h3>
        <p className="owner-card__agency">
          <i className="fa-solid fa-briefcase" aria-hidden="true" />
          {/* TODO: backend-truth-missing — no Agency model on
              Property or User. Slot preserved per canonical layout. */}
          <span className="muted">—</span>
        </p>
        {/* Contacts block is hidden for owner-on-own-listing
            (canContact === false). */}
        {canContact ? (
          <div className="owner-card__contacts">
            {phone ? (
              <a
                href={`tel:${phone}`}
                className="btn-more btn-tel"
                aria-label={`Зателефонувати ${phone}`}
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>{phone}</span>
              </a>
            ) : null}

            {email ? (
              <a
                href={`mailto:${email}`}
                className="btn-more btn-tel"
                aria-label={`Написати на ${email}`}
              >
                <i className="fa-solid fa-envelope" aria-hidden="true" />
                <span>{email}</span>
              </a>
            ) : null}

            {isAuthenticated ? (
              <button
                type="button"
                className="btn-more btn-chat"
                onClick={handleChatClick}
                disabled={chatPending || ownerUserId == null}
                aria-busy={chatPending ? "true" : undefined}
                aria-label="Написати в чат"
              >
                <i className="fa-solid fa-comments" aria-hidden="true" />
                {chatPending ? "Створення…" : "Написати в чат"}
              </button>
            ) : (
              <Link
                to={loginHref}
                className="btn-more btn-chat"
                aria-label="Увійти, щоб написати"
              >
                <i className="fa-solid fa-comments" aria-hidden="true" />
                Увійти, щоб написати
              </Link>
            )}

            {chatError ? (
              <p className="owner-card__chat-error" role="alert">
                {chatError}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
}

export default DetailOwnerCard;
